import { Alert, Button, Drawer, Flex, Space } from 'antd';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

export interface ActionDrawerProps {
  open: boolean;
  title: string;
  /** Names the outcome, not the mechanism: "Create customer", not "Submit". */
  submitLabel: string;
  onSubmit: () => void;
  onClose: () => void;
  submitting?: boolean;
  submitDisabled?: boolean;
  /**
   * What will change if the user confirms. CLAUDE.md §10: every destructive or
   * approval action shows what will change before confirming.
   */
  consequence?: ReactNode;
  danger?: boolean;
  error?: ReactNode;
  /** Replaces the Cancel and submit buttons, for a done state that has its own. */
  footer?: ReactNode;
  children: ReactNode;
}

/**
 * The third screen primitive (CLAUDE.md §10). Full height on a phone, a side
 * panel on a desktop, with the submit control pinned so it is reachable without
 * scrolling a long form on a small screen.
 */
export function ActionDrawer({
  open,
  title,
  submitLabel,
  onSubmit,
  onClose,
  submitting = false,
  submitDisabled = false,
  consequence,
  danger = false,
  error,
  footer,
  children,
}: ActionDrawerProps): ReactNode {
  const { t } = useTranslation();

  return (
    <Drawer
      open={open}
      title={title}
      onClose={onClose}
      // A payment in flight cannot be walked away from: its result belongs to this drawer.
      closable={!submitting}
      maskClosable={!submitting}
      keyboard={!submitting}
      width={480}
      styles={{ body: { paddingBottom: 8 } }}
      // Full-bleed on a phone; a 480px panel on a 390px screen is a bug.
      className="nks-action-drawer"
      footer={
        footer ?? (
          <Flex justify="flex-end" gap={8}>
            <Space>
              <Button onClick={onClose} disabled={submitting} size="large">
                {t('common.cancel')}
              </Button>
              <Button
                type="primary"
                danger={danger}
                onClick={onSubmit}
                loading={submitting}
                disabled={submitDisabled}
                size="large"
              >
                {submitLabel}
              </Button>
            </Space>
          </Flex>
        )
      }
    >
      <Flex vertical gap={16}>
        {error}
        {children}
        {consequence !== undefined && (
          <Alert type={danger ? 'warning' : 'info'} message={consequence} showIcon />
        )}
      </Flex>
    </Drawer>
  );
}
